<template>
  <section class="awb-card" :aria-label="$t('airwallexBalance.transactions')">
    <div v-if="uncredited.length" class="awb-alert-block mb-4" role="region" aria-labelledby="awb-uncredited">
      <h2 id="awb-uncredited" class="awb-h3 mb-1">{{ $t("airwallexBalance.uncreditedTitle") }}</h2>
      <p class="small mb-3">{{ $t("airwallexBalance.uncreditedNote") }}</p>
      <div class="awb-table-wrap">
        <table class="awb-table">
          <thead>
            <tr>
              <th scope="col">{{ $t("airwallexBalance.paidAt") }}</th>
              <th scope="col">{{ $t("airwallexBalance.order") }}</th>
              <th scope="col">{{ $t("airwallexBalance.airwallexPayment") }}</th>
              <th v-if="multiAccount" scope="col">{{ $t("airwallexBalance.account") }}</th>
              <th scope="col">{{ $t("airwallexBalance.colMethod") }}</th>
              <th scope="col" class="awb-right">{{ $t("airwallexBalance.customerPaid") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in uncredited" :key="row.id">
              <td class="text-nowrap">{{ dateTime(row.paid_at) }}</td>
              <td class="fw-bold">{{ row.transaction_id || "—" }}</td>
              <td class="text-break awb-muted">{{ row.payment_intent_id }}</td>
              <td v-if="multiAccount">{{ row.branch_name || $t("airwallexBalance.merchantAccount") }}</td>
              <td>{{ methodLabel(row.payment_method) }}</td>
              <td class="awb-right awb-num fw-bold">{{ money(row.amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="d-flex flex-wrap gap-2 mb-3" role="group" :aria-label="$t('airwallexBalance.colType')">
      <button
        v-for="chip in chips"
        :key="chip.id || 'all'"
        type="button"
        class="awb-chip"
        :aria-pressed="category === chip.id ? 'true' : 'false'"
        @click="filterBy(chip.id)"
      >
        {{ $t(chip.label) }}
      </button>
    </div>

    <div class="d-flex flex-wrap gap-3 align-items-end mb-4">
      <label class="awb-field flex-grow-1">
        {{ $t("airwallexBalance.search") }}
        <input v-model.trim="search" type="search" class="awb-input" :placeholder="$t('airwallexBalance.searchPlaceholder')" @change="getList(1)" />
      </label>
      <label class="awb-field">
        {{ $t("airwallexBalance.colStatus") }}
        <select v-model="status" class="awb-select" @change="getList(1)">
          <option value="">{{ $t("airwallexBalance.allStatuses") }}</option>
          <option value="PENDING">{{ $t("airwallexBalance.statusPending") }}</option>
          <option value="SETTLED">{{ $t("airwallexBalance.statusSettled") }}</option>
          <option value="CANCELLED">{{ $t("airwallexBalance.statusCancelled") }}</option>
        </select>
      </label>
      <label class="awb-field">
        {{ $t("airwallexBalance.from") }}
        <input v-model="from" type="date" class="awb-input" :max="to || null" @change="getList(1)" />
      </label>
      <label class="awb-field">
        {{ $t("airwallexBalance.to") }}
        <input v-model="to" type="date" class="awb-input" :min="from || null" @change="getList(1)" />
      </label>
      <button type="button" class="awb-btn" :disabled="exporting" @click="exportCsv">{{ $t("airwallexBalance.exportCsv") }}</button>
    </div>
    <p v-if="exportError" class="awb-note awb-note--warn mb-3" role="alert">{{ exportError }}</p>

    <p v-if="loaded && !rows.length" class="awb-empty mb-0">{{ $t("airwallexBalance.empty") }}</p>
    <div v-else class="awb-table-wrap">
      <table class="awb-table awb-table--wide">
        <thead>
          <tr>
            <th scope="col">{{ $t("airwallexBalance.colDate") }}</th>
            <th scope="col">{{ $t("airwallexBalance.colType") }}</th>
            <th scope="col">{{ $t("airwallexBalance.colOrder") }}</th>
            <th scope="col">{{ $t("airwallexBalance.colMethod") }}</th>
            <th scope="col">{{ $t("airwallexBalance.colStatus") }}</th>
            <th scope="col" class="awb-right">{{ $t("airwallexBalance.colGross") }}</th>
            <th scope="col" class="awb-right">{{ $t("airwallexBalance.colFee") }}</th>
            <th scope="col" class="awb-right">{{ $t("airwallexBalance.colNet") }}</th>
            <th scope="col">{{ $t("airwallexBalance.colSettled") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id" class="awb-row" @click="$emit('open', row.id)">
            <td class="text-nowrap">{{ dateTime(row.created_at) }}</td>
            <td>
              {{ $t(typeKey[row.category]) }}
              <div v-if="row.branch_name || multiAccount" class="small awb-muted">
                {{ row.branch_name || $t("airwallexBalance.merchantAccount") }}
              </div>
            </td>
            <td>
              <button type="button" class="awb-link text-start" @click.stop="$emit('open', row.id)">{{ rowRef(row) }}</button>
            </td>
            <td class="awb-muted">{{ row.category === "payout" ? $t("airwallexBalance.payoutMethod") : methodLabel(row.payment_method) }}</td>
            <td><span class="awb-badge" :class="statusClass(row)">{{ $t(statusKey(row)) }}</span></td>
            <td class="awb-right awb-num">{{ money(row.gross_amount) }}</td>
            <td class="awb-right awb-num awb-muted">{{ money(row.merchant_fee) }}</td>
            <td class="awb-right awb-num fw-bold">{{ money(row.amount) }}</td>
            <td class="text-nowrap awb-muted">{{ dateTime(row.settled_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination.total" class="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
      <span class="small awb-muted">
        {{ $t("airwallexBalance.showing", { from: pagination.from, to: pagination.to, total: pagination.total }) }}
      </span>
      <pagination
        v-if="pagination.last_page > 1"
        :pagination="pagination"
        :current_page="pagination.current_page"
        :last_page="pagination.last_page"
      />
    </div>
  </section>
</template>

<script>
import moment from "moment";
import PaymentService from "../../../services/payment.service";
import Pagination from "@/components/Pagination.vue";
import { money, dateTime, methodLabel, typeKey, statusKey, statusClass, rowRef } from "./format";

export default {
  name: "balance-transactions",
  components: { Pagination },
  props: {
    branchId: { type: Number, default: null },
    // Name the merchant-level wallet on its rows too when there are several.
    multiAccount: { type: Boolean, default: false },
  },
  emits: ["open"],
  data() {
    return {
      chips: [
        { id: null, label: "airwallexBalance.all" },
        { id: "payment", label: "airwallexBalance.payments" },
        { id: "payout", label: "airwallexBalance.payouts" },
        { id: "refund", label: "airwallexBalance.refundsTab" },
      ],
      category: null,
      search: "",
      status: "",
      from: "",
      to: "",
      rows: [],
      uncredited: [],
      pagination: {},
      loaded: false,
      // Filters can change faster than the API answers: only the latest request fills the table.
      listRequest: 0,
      exporting: false,
      exportError: "",
      typeKey,
    };
  },
  watch: {
    branchId() {
      this.getList(1);
      this.loadUncredited();
    },
  },
  mounted() {
    this.getList(1);
    this.loadUncredited();
  },
  methods: {
    money,
    dateTime,
    methodLabel,
    statusKey,
    statusClass,
    rowRef,
    // Also called by the page ("See all", "View payouts").
    filterBy(category) {
      this.category = category;
      this.getList(1);
    },
    params() {
      const params = {};
      if (this.branchId !== null) params.physical_branch_id = this.branchId;
      if (this.category) params.category = this.category;
      if (this.status) params.status = this.status;
      if (this.from) params.from = this.from;
      if (this.to) params.to = this.to;
      if (this.search) params.search = this.search;
      return params;
    },
    // Pagination.vue calls $parent.getList(page).
    getList(page = 1) {
      const request = ++this.listRequest;
      PaymentService.airwallexBalanceTransactions({ ...this.params(), page }).then((response) => {
        if (request !== this.listRequest) return;
        if (response.data.result.isSuccess) {
          const data = response.data.data || {};
          this.rows = data.data || [];
          this.pagination = data;
        }
        this.loaded = true;
      });
    },
    loadUncredited() {
      const params = this.branchId === null ? {} : { physical_branch_id: this.branchId };
      PaymentService.airwallexUncreditedPayments(params).then((response) => {
        if (response.data.result.isSuccess) {
          this.uncredited = response.data.data || [];
        }
      });
    },
    async exportCsv() {
      this.exporting = true;
      this.exportError = "";
      try {
        const response = await PaymentService.airwallexExportBalanceTransactions(this.params());
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), response.data], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `airwallex_transactions_${moment().format("YYYYMMDD_HHmm")}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        // A refused export (e.g. too many rows) comes back as a JSON blob.
        let message = "";
        try {
          message = JSON.parse(await error.response.data.text()).result.message;
        } catch (e) {
          message = "";
        }
        this.exportError = message || this.$t("airwallexBalance.loadFailed");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
